import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  Film,
  ImagePlus,
  Loader2,
  Upload,
  Video as VideoIcon,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Textarea } from '@/components/ui/textarea'
import { uploadFile } from '@/shared/api/app-client'
import { fetchMembershipSummary } from '@/features/auth/services/auth-service'
import { useMembershipFeatureGate } from '@/features/billing/hooks/use-membership-feature-gate'
import { useCreditRechargeDialogStore } from '@/features/billing/stores/credit-recharge-dialog-store'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import type { ApiResponse, AssetUploadResult } from '@/shared/api/app-types'
import {
  RESOLUTION_OPTIONS,
  RATIO_OPTIONS,
  VIDEO_MODEL_OPTIONS,
  aliyunVideoService,
  getEstimatedVideoCredits,
  getVideoModelOption,
  type VideoMode,
  type VideoRatio,
  type VideoResolution,
} from '@/features/video-gen/services/aliyun-video-service'
import { DemoVideosSection } from '@/features/app-shell/canvases/digital-human-canvas'

/** 格式化文件大小 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function AiVideoGenCanvas() {
  const navigate = useNavigate()
  const [model, setModel] = useState('wan2.7-t2v')
  const [prompt, setPrompt] = useState(
    '一只橘色的猫坐在窗台上，窗外是下雨的城市夜景，霓虹灯光映在玻璃上，猫转头看向窗外，电影感运镜',
  )
  const [negativePrompt, setNegativePrompt] = useState('')
  const [imageFiles, setImageFiles] = useState<File[]>([])
  const [imagePreviews, setImagePreviews] = useState<string[]>([])
  const [resolution, setResolution] = useState<VideoResolution>('720P')
  const [ratio, setRatio] = useState<VideoRatio>('16:9')
  const [duration, setDuration] = useState(5)
  const [seed, setSeed] = useState('')
  const [promptExtend, setPromptExtend] = useState(true)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savedAsset, setSavedAsset] = useState<AssetUploadResult | null>(null)
  const [result, setResult] = useState<{
    file: File
    objectUrl: string
  } | null>(null)
  const videoCreditGate = useMembershipFeatureGate({
    requiredQuotaMetric: 'generation_credits',
    missingMessage: 'AI 视频生成需要先购买高级生成点数。',
    quotaExceededMessage: '高级生成点数不足，请先充值后再生成视频。',
  })
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const openCreditRechargeDialog = useCreditRechargeDialogStore((state) => state.openDialog)
  const generationCredits = useAuthSessionStore((state) => state.session?.membership?.quotas?.details?.generation_credits?.available)

  const selectedModel = useMemo(() => getVideoModelOption(model), [model])
  const mode: VideoMode = selectedModel?.mode ?? 't2v'
  const needsImages = mode === 'i2v' || mode === 'r2v'
  const maxImages = mode === 'i2v' ? 1 : 5
  const canGenerate =
    prompt.trim().length > 0 &&
    !isGenerating &&
    (!needsImages || imageFiles.length > 0) &&
    !!selectedModel

  function handleModelChange(value: string) {
    setModel(value)
    // 切换模式时清空图片
    if (imageFiles.length > 0) {
      imagePreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
      setImageFiles([])
      setImagePreviews([])
    }
    // 切换模型时调整时长到合法范围
    const newModel = getVideoModelOption(value)
    if (newModel) {
      if (duration < newModel.minDuration) setDuration(newModel.minDuration)
      if (duration > newModel.maxDuration) setDuration(newModel.maxDuration)
    }
  }

  function handleImageUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0) return

    const remaining = maxImages - imageFiles.length
    const toAdd = files.slice(0, remaining)
    if (toAdd.length === 0) return

    const newPreviews = toAdd.map((f) => URL.createObjectURL(f))
    setImageFiles((prev) => [...prev, ...toAdd])
    setImagePreviews((prev) => [...prev, ...newPreviews])
    event.target.value = ''
  }

  function removeImage(index: number) {
    const url = imagePreviews[index]
    if (url) URL.revokeObjectURL(url)
    setImageFiles((prev) => prev.filter((_, i) => i !== index))
    setImagePreviews((prev) => prev.filter((_, i) => i !== index))
  }

  const handleGenerate = async () => {
    if (!canGenerate || !selectedModel) return
    if (!videoCreditGate.ensureAccess()) {
      setError('高级生成点数不足，请先充值后再生成视频。')
      return
    }

    setIsGenerating(true)
    setError(null)
    setProgress(null)
    setSavedAsset(null)

    try {
      if (result) URL.revokeObjectURL(result.objectUrl)

      const generated = await aliyunVideoService.generateVideo({
        model: selectedModel.value,
        prompt,
        negativePrompt: selectedModel.supportsNegativePrompt ? negativePrompt.trim() || undefined : undefined,
        imageFiles: needsImages ? imageFiles : undefined,
        resolution,
        ratio: mode === 'i2v' ? undefined : ratio, // i2v 不支持 ratio
        duration,
        seed: seed.trim() ? Number(seed) : undefined,
        promptExtend: selectedModel.supportsPromptExtend ? promptExtend : undefined,
        onProgress: setProgress,
      })

      setResult({ file: generated.file, objectUrl: generated.objectUrl })
      // 高成本生成成功后立即刷新点数与套餐额度，避免用户离开当前页面才能看到余额变化。
      void fetchMembershipSummary()
        .then((membership) => {
          if (membership) updateMembership(membership)
        })
        .catch(() => {
          // 不因摘要刷新失败掩盖已经成功返回的视频结果。
        })
      setProgress('视频生成完成，可以下载或保存到素材库。')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'AI 视频生成失败。'
      // 前端校验与服务端实际扣点之间可能存在并发或余额更新延迟；服务端再次
      // 拒绝时也应回到点数充值，而不是把用户留在一条不能继续操作的报错上。
      if (/高级生成点数不足|generation_credits|quota.*exceed/i.test(message)) {
        openCreditRechargeDialog()
        setError('高级生成点数不足，请先充值后再生成视频。')
      } else {
        setError(message)
      }
      setProgress(null)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDownload = () => {
    if (!result) return
    const anchor = document.createElement('a')
    anchor.href = result.objectUrl
    anchor.download = result.file.name
    anchor.click()
  }

  const handleSaveAsset = async () => {
    if (!result || isSaving) return
    setIsSaving(true)
    setError(null)
    try {
      const uploadRes = await uploadFile<ApiResponse<AssetUploadResult>>(
        '/api/assets/upload',
        result.file,
      )
      setSavedAsset(uploadRes.data)
      setProgress('已保存到素材库。')
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存到素材库失败。')
    } finally {
      setIsSaving(false)
    }
  }

  // 预估费用
  const selectedPricePerSec = selectedModel
    ? resolution === '1080P' ? selectedModel.pricePerSec1080 : selectedModel.pricePerSec
    : 0
  const estimatedCost = (selectedPricePerSec * duration).toFixed(1)
  const estimatedCredits = getEstimatedVideoCredits(selectedModel, resolution, duration)

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center gap-4">
        <Link
          to="/app"
          className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-card text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="size-4" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">AI 生成视频</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            万相2.7与快乐马视频生成，支持文生、图生、参考生三种模式。
          </p>
        </div>
      </div>

      <div className="grid gap-8 xl:grid-cols-[1.15fr_0.85fr]">
        {/* Left: generation settings */}
        <section className="space-y-6">
          {/* Model selector */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">视频模型</Label>
            <Select value={model} onValueChange={handleModelChange}>
              <SelectTrigger>
                <SelectValue placeholder="选择模型" />
              </SelectTrigger>
              <SelectContent>
                {(['wan', 'happyhorse'] as const).map((fam) => {
                  const options = VIDEO_MODEL_OPTIONS.filter((o) => o.family === fam)
                  const firstOption = options[0]
                  if (options.length === 0 || !firstOption) return null
                  return (
                    <SelectGroup key={fam}>
                      <SelectLabel>{firstOption.familyLabel}</SelectLabel>
                      {options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label} · 720P ¥{option.pricePerSec}/秒 · 1080P ¥{option.pricePerSec1080}/秒
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  )
                })}
              </SelectContent>
            </Select>
            {selectedModel ? (
              <div className="space-y-1.5">
                <p className="text-xs leading-5 text-muted-foreground">
                  {selectedModel.description}
                </p>
                <p className="text-[11px] leading-4 text-muted-foreground/70">
                  {selectedModel.familyDescription}
                </p>
              </div>
            ) : null}
          </div>

          {/* Image upload (i2v / r2v) */}
          {needsImages ? (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {mode === 'i2v' ? '首帧图片' : '参考图片'}
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                  （{mode === 'i2v' ? '1 张' : `最多 ${maxImages} 张`}）
                </span>
              </Label>
              <div className="flex flex-wrap gap-3">
                {imagePreviews.map((url, i) => (
                  <div
                    key={i}
                    className="group relative size-24 overflow-hidden rounded-lg border border-border"
                  >
                    <img src={url} alt={`图片 ${i + 1}`} className="size-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute right-1 top-1 flex size-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <X className="size-3" />
                    </button>
                    {mode === 'r2v' ? (
                      <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[10px] text-white">
                        图{i + 1}
                      </span>
                    ) : null}
                  </div>
                ))}
                {imageFiles.length < maxImages ? (
                  <label className="flex size-24 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border bg-card transition-colors hover:bg-accent">
                    <ImagePlus className="size-5 text-muted-foreground/50" />
                    <span className="text-[10px] text-muted-foreground">上传图片</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/jpeg,image/png,image/webp,image/bmp"
                      multiple={mode === 'r2v'}
                      onChange={handleImageUpload}
                    />
                  </label>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">
                {mode === 'i2v'
                  ? '上传一张图片作为视频首帧，视频比例将跟随图片。'
                  : '上传参考图片，在提示词中用「图1」「图2」引用。'}
              </p>
            </div>
          ) : null}

          {/* Prompt */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="video-prompt" className="text-sm font-medium">
                视频描述
              </Label>
              <span className="text-xs text-muted-foreground">{prompt.trim().length} 字</span>
            </div>
            <Textarea
              id="video-prompt"
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder="描述你想生成的视频内容，例如：一只橘色的猫坐在窗台上，窗外下雨的城市夜景…"
              className="min-h-[120px] resize-y"
            />
          </div>

          {/* Negative prompt (only for models that support it) */}
          {selectedModel?.supportsNegativePrompt ? (
            <div className="space-y-3">
              <Label htmlFor="video-negative" className="text-sm font-medium">
                负面提示词
                <span className="ml-1.5 text-xs font-normal text-muted-foreground">（可选）</span>
              </Label>
              <Input
                id="video-negative"
                value={negativePrompt}
                onChange={(event) => setNegativePrompt(event.target.value)}
                placeholder="不想出现的内容，如：模糊、变形、低质量"
              />
            </div>
          ) : null}

          {/* Resolution + Ratio */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-3">
              <Label className="text-sm font-medium">分辨率</Label>
              <Select
                value={resolution}
                onValueChange={(value) => setResolution(value as VideoResolution)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <Label className="text-sm font-medium">
                比例
                {mode === 'i2v' ? (
                  <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                    （跟随图片）
                  </span>
                ) : null}
              </Label>
              <Select
                value={ratio}
                onValueChange={(value) => setRatio(value as VideoRatio)}
                disabled={mode === 'i2v'}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RATIO_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Duration slider */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">视频时长</Label>
              <span className="text-sm font-medium text-foreground">{duration} 秒</span>
            </div>
            <Slider
              value={[duration]}
              onValueChange={(values) => {
                const value = values[0]
                if (typeof value === 'number') setDuration(value)
              }}
              min={selectedModel?.minDuration ?? 2}
              max={selectedModel?.maxDuration ?? 15}
              step={1}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{selectedModel?.minDuration ?? 2}秒</span>
              <span>{resolution} 预估模型成本约 ¥{estimatedCost}</span>
              <span>{selectedModel?.maxDuration ?? 15}秒</span>
            </div>
          </div>

          {/* Advanced parameters */}
          <div className="space-y-4 rounded-lg border border-border bg-card p-4">
            <div className="text-sm font-medium text-foreground">高级参数</div>

            {/* Seed */}
            <div className="space-y-1.5">
              <Label htmlFor="video-seed" className="text-xs text-muted-foreground">
                随机种子
                <span className="ml-1 text-muted-foreground/70">（可选，固定可提升可复现性）</span>
              </Label>
              <Input
                id="video-seed"
                value={seed}
                onChange={(event) => setSeed(event.target.value)}
                placeholder="留空则随机"
                inputMode="numeric"
                className="h-8"
              />
            </div>

            {/* Prompt extend (wan only) */}
            {selectedModel?.supportsPromptExtend ? (
              <label className="flex cursor-pointer items-center justify-between">
                <div>
                  <span className="text-xs text-foreground">提示词智能改写</span>
                  <p className="text-[11px] text-muted-foreground">开启后对短提示词效果提升明显</p>
                </div>
                <input
                  type="checkbox"
                  checked={promptExtend}
                  onChange={(event) => setPromptExtend(event.target.checked)}
                  className="size-4 accent-primary"
                />
              </label>
            ) : null}
          </div>

          {/* Generate */}
          <Button
            onClick={() => void handleGenerate()}
            disabled={!canGenerate}
            className="w-full sm:w-auto"
          >
            {isGenerating ? <Loader2 className="size-4 animate-spin" /> : <Film className="size-4" />}
            生成视频
          </Button>
          <p className="text-xs text-muted-foreground">
            本次预计预占 {estimatedCredits} 点；当前剩余 {generationCredits ?? '—'} 点。不同模型与分辨率价格不同，成功后按实际输出时长结算。
          </p>

          {progress ? (
            <div className="rounded-lg border border-primary/15 bg-primary/5 px-3.5 py-2.5 text-sm text-primary">
              {progress}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">
              {error}
            </div>
          ) : null}
        </section>

        {/* Right: preview */}
        <section className="space-y-5">
          {result ? (
            <>
              <div className="space-y-3 rounded-xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {selectedModel?.label ?? model}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {resolution} · {mode === 'i2v' ? '跟随图片比例' : ratio} · {duration}秒 ·{' '}
                      {formatFileSize(result.file.size)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                    <VideoIcon className="size-5" />
                  </div>
                </div>

                <video
                  controls
                  src={result.objectUrl}
                  className="w-full rounded-lg border border-border"
                />
              </div>

              {/* Result actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleDownload} disabled={!result}>
                  <Download className="size-4" />
                  下载视频
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => void handleSaveAsset()}
                  disabled={!result || isSaving}
                >
                  {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                  保存到素材库
                </Button>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/app/assets', search: { tab: 'video' } })}
                >
                  去素材库查看
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate({ to: '/projects/new' })}
                >
                  新建项目并进入编辑器
                </Button>
              </div>

              {savedAsset ? (
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3.5 py-2.5 text-sm text-emerald-700">
                  <div className="flex items-center gap-2 font-medium">
                    <CheckCircle2 className="size-4" />
                    已保存到素材库
                  </div>
                  <div className="mt-1 text-xs text-emerald-700/80">
                    素材 ID: {savedAsset.id} · 文件名: {savedAsset.fileName}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-border px-8 text-center">
              {isGenerating ? (
                <Loader2 className="size-8 animate-spin text-muted-foreground/50" />
              ) : (
                <VideoIcon className="size-8 text-muted-foreground/50" />
              )}
              <h3 className="mt-4 text-sm font-medium text-foreground">
                {isGenerating ? '视频生成中，请耐心等待…' : '生成结果会在这里展示'}
              </h3>
              <p className="mt-1.5 max-w-xs text-xs leading-5 text-muted-foreground">
                {isGenerating
                  ? '视频生成通常需要 1-5 分钟，请勿离开页面。'
                  : '在左侧选择模式、填写描述后点击生成。'}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* 演示视频展示 */}
      <div className="mt-12">
        <DemoVideosSection title="看看 AI 生成的视频效果" />
      </div>
    </div>
  )
}
